// Durum makineleri (BUILD-PLAN): Trip, çocuk, şikayet, talep.
import type { BoardingType, ComplaintStatus, Direction, RequestStatus, TripStatus } from './enums';

// ───────── Sefer ─────────

const TRIP_TRANSITIONS: Record<TripStatus, TripStatus[]> = {
  SCHEDULED: ['READY', 'IN_PROGRESS', 'CANCELLED'],
  READY: ['IN_PROGRESS', 'CANCELLED', 'SCHEDULED'],
  IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
  COMPLETED: [],
  CANCELLED: [],
};

export function canTransitionTrip(from: TripStatus, to: TripStatus): boolean {
  return TRIP_TRANSITIONS[from].includes(to);
}

export function assertTripTransition(from: TripStatus, to: TripStatus): void {
  if (!canTransitionTrip(from, to)) throw new Error(`Sefer durumu ${from} → ${to} geçişine izin yok`);
}

export interface TripFlags {
  delayed: boolean;
  critical: boolean;
}

/** DELAYED ve CRITICAL bayrakları türetilir (kalıcı durum değildir). */
export function tripFlags(
  t: { status: TripStatus; delayMin: number; openCriticalAlerts?: number },
  delayWarnMin = 5,
): TripFlags {
  const live = t.status === 'IN_PROGRESS' || t.status === 'READY';
  return { delayed: live && t.delayMin >= delayWarnMin, critical: live && (t.openCriticalAlerts ?? 0) > 0 };
}

export type TripDisplay = 'NOT_STARTED' | 'PREPARING' | 'ON_TIME' | 'DELAYED' | 'CRITICAL' | 'COMPLETED' | 'CANCELLED';

export const TRIP_DISPLAY_LABELS: Record<TripDisplay, string> = {
  NOT_STARTED: 'Başlamadı',
  PREPARING: 'Hazırlanıyor',
  ON_TIME: 'Yolda',
  DELAYED: 'Gecikmeli',
  CRITICAL: 'Kritik',
  COMPLETED: 'Tamamlandı',
  CANCELLED: 'İptal',
};

export function tripDisplay(
  t: { status: TripStatus; delayMin: number; openCriticalAlerts?: number },
  delayWarnMin = 5,
): TripDisplay {
  if (t.status === 'CANCELLED') return 'CANCELLED';
  if (t.status === 'COMPLETED') return 'COMPLETED';
  const f = tripFlags(t, delayWarnMin);
  if (f.critical) return 'CRITICAL';
  if (t.status === 'SCHEDULED') return 'NOT_STARTED';
  if (t.status === 'READY') return 'PREPARING';
  return f.delayed ? 'DELAYED' : 'ON_TIME';
}

// ───────── Çocuk ─────────

export type ChildState =
  'AT_HOME' | 'WAITING' | 'BOARDED' | 'EN_ROUTE' | 'ARRIVED' | 'AT_SCHOOL' | 'DELIVERED' | 'NOT_BOARDED' | 'ABSENT';

export const CHILD_STATE_LABELS: Record<ChildState, string> = {
  AT_HOME: 'Evde',
  WAITING: 'Servis bekleniyor',
  BOARDED: 'Servise bindi',
  EN_ROUTE: 'Yolda',
  ARRIVED: 'Okula ulaştı',
  AT_SCHOOL: 'Okulda',
  DELIVERED: 'Teslim edildi',
  NOT_BOARDED: 'Binmedi',
  ABSENT: 'Bugün binmeyecek',
};

/** Gidiş sırası (P45 ilerleme şeridi): Evde → Bekleniyor → Bindi → Yolda → Okulda. */
export const CHILD_OUTBOUND_STEPS: ChildState[] = ['AT_HOME', 'WAITING', 'BOARDED', 'EN_ROUTE', 'ARRIVED'];
/** Dönüş sırası: Okulda → Bindi → Yolda → Teslim edildi. */
export const CHILD_INBOUND_STEPS: ChildState[] = ['AT_SCHOOL', 'BOARDED', 'EN_ROUTE', 'DELIVERED'];

export interface ChildStateInput {
  direction: Direction;
  tripStatus: TripStatus | null;
  absent?: boolean;
  /** çocuğun bu seferdeki olayları, zaman sırasıyla */
  events: { type: BoardingType; at: number }[];
  /** biniş durağından kalkıldı mı */
  departedBoardingStop?: boolean;
}

export function deriveChildState(i: ChildStateInput): ChildState {
  if (i.absent) return 'ABSENT';
  const last = i.events.at(-1)?.type;
  if (i.direction === 'OUTBOUND') {
    if (last === 'ALIGHTED' || last === 'DELIVERED') return 'ARRIVED';
    if (last === 'NO_SHOW' || last === 'NOT_BOARDED') return 'NOT_BOARDED';
    if (last === 'BOARDED') {
      if (i.tripStatus === 'COMPLETED') return 'ARRIVED';
      return i.departedBoardingStop ? 'EN_ROUTE' : 'BOARDED';
    }
    if (i.tripStatus === 'IN_PROGRESS' || i.tripStatus === 'READY') return 'WAITING';
    return 'AT_HOME';
  }
  if (last === 'DELIVERED' || last === 'ALIGHTED') return 'DELIVERED';
  if (last === 'NO_SHOW' || last === 'NOT_BOARDED') return 'NOT_BOARDED';
  if (last === 'BOARDED') return i.departedBoardingStop ? 'EN_ROUTE' : 'BOARDED';
  return 'AT_SCHOOL';
}

// ───────── Şikayet ─────────

const COMPLAINT_TRANSITIONS: Record<ComplaintStatus, ComplaintStatus[]> = {
  NEW: ['REVIEWING', 'RESOLVED', 'REJECTED'],
  REVIEWING: ['WAITING_DRIVER', 'RESOLVED', 'REJECTED', 'NEW'],
  WAITING_DRIVER: ['REVIEWING', 'RESOLVED', 'REJECTED'],
  RESOLVED: ['REVIEWING'],
  REJECTED: ['REVIEWING'],
};

export function canTransitionComplaint(from: ComplaintStatus, to: ComplaintStatus): boolean {
  return from === to || COMPLAINT_TRANSITIONS[from].includes(to);
}

// ───────── Talep ─────────

const REQUEST_TRANSITIONS: Record<RequestStatus, RequestStatus[]> = {
  NEW: ['REVIEWING', 'INSTITUTION_APPROVAL', 'APPROVED', 'REJECTED'],
  REVIEWING: ['INSTITUTION_APPROVAL', 'APPROVED', 'REJECTED'],
  INSTITUTION_APPROVAL: ['APPROVED', 'REJECTED', 'REVIEWING'],
  APPROVED: [],
  REJECTED: ['REVIEWING'],
};

export function canTransitionRequest(from: RequestStatus, to: RequestStatus): boolean {
  return REQUEST_TRANSITIONS[from].includes(to);
}
